/**
 * Mounting the help widget in the portal (plan-019 WAPP-HOST; FR-HA-1, FR-HA-2, FR-HA-5; OD-3; design §31.8.3, §31.9.1,
 * §31.9.6).
 *
 * The widget is mounted, after the page is idle (`requestIdleCallback`, so it never delays the first paint), when:
 *
 * - help is on — `GET /v1/public/help/status` says `enabled` (the sandbox and production ship it off until AP-4);
 * - the sign-in is known, and it is either nobody (a way-in page: the widget runs in anonymous mode) or the person
 *   themself inside an organization — **never while an operator acts for the person** (impersonation unmounts it; the
 *   grant route refuses anyway), and not on the organization picker, where the api's help routes have no organization;
 * - the display language is English or Greek (no fallback: another language mounts nothing).
 *
 * The launcher module is imported only then (`@wetalk/help-widget`, its own lazily loaded chunk); a refused import, or
 * a widget a blocker hides, makes the person menu's Help item open the plain `/help` page instead (OD-24). Both help
 * imports — the launcher here and the panel the widget asks for on first open — run marked as help imports
 * (`./help-chunk.ts`), so the console's stale-build guard leaves their failure alone instead of reloading the page and
 * resolving the import `undefined` (plan-019 E2E fix); a module that still arrives without its entry is refused too.
 * The loader itself reloads once when the failure was a deploy (the served build differs from this tab's), so only a
 * same-build refusal — a content blocker — ends as "unavailable" (plan-019 REVIEW-FIX).
 */
import { isHelpLanguage } from "@wetalk/domain";
import type { HelpHostApi, HelpWidgetEnvironment, HelpWidgetHandle } from "@wetalk/help-widget";
import { useEffect, type ReactNode } from "react";

import type { AuthSessionView } from "../api/gateway.js";
import { loadHelpChunk } from "./help-chunk.js";
import type { PortalHelpHost } from "./host-api.js";

/** How the widget is shown now. */
export type HelpMountMode = "hidden" | "signed_in" | "anonymous";

/**
 * Whether and how the widget is mounted: `status` is the help status (`undefined` while unknown or unreadable), `current`
 * the auth_session (`undefined` while unknown, `null` signed out), `language` the portal's display language.
 */
export function helpMountModeOf(
  status: { readonly enabled: boolean } | undefined,
  current: AuthSessionView | null | undefined,
  language: string,
): HelpMountMode {
  if (status === undefined || !status.enabled || current === undefined || !isHelpLanguage(language)) return "hidden";
  if (current === null) return "anonymous";
  if (current.actor.kind !== "self" || current.kind === "selection_required") return "hidden";
  return "signed_in";
}

/** The launcher module's entry, as the mount uses it. */
export interface HelpWidgetModule {
  readonly mountHelpWidget: (host: PortalHelpHost) => HelpWidgetHandle | null;
}

/** The parts of `@wetalk/help-widget` the browser loader composes. */
interface HelpWidgetPackage {
  readonly mountHelpWidget: (host: HelpHostApi, environment: HelpWidgetEnvironment) => HelpWidgetHandle | null;
  readonly browserEnvironment: () => HelpWidgetEnvironment;
}

/** A module that is not there (a guard that swallowed the failure resolves the import `undefined`) is a refusal. */
function present<T extends object>(module: T | undefined, what: string): T {
  if (typeof module !== "object") throw new Error(`The help ${what} chunk did not load.`);
  return module;
}

/** Runs `run` once the browser is idle; returns the cancel. */
export type IdleScheduler = (run: () => void) => () => void;

export function browserIdleScheduler(): IdleScheduler {
  return (run) => {
    const view = globalThis.window;
    if (typeof view.requestIdleCallback === "function") {
      const handle = view.requestIdleCallback(run);
      return () => {
        view.cancelIdleCallback(handle);
      };
    }
    // A browser without idle callbacks: after the current task, which is after the first paint.
    const handle = view.setTimeout(run, 0);
    return () => {
      view.clearTimeout(handle);
    };
  };
}

export async function loadHelpWidgetModule(): Promise<HelpWidgetModule> {
  const widget = present<HelpWidgetPackage>(await loadHelpChunk(() => import("@wetalk/help-widget")), "launcher");
  return {
    mountHelpWidget: (host) => {
      const browser = widget.browserEnvironment();
      // The panel's import (the widget's one dynamic import), marked as a help import too.
      return widget.mountHelpWidget(host, { ...browser, loadPanel: async () => present(await loadHelpChunk(browser.loadPanel), "panel") });
    },
  };
}

export interface HelpMountProps {
  readonly mounted: boolean;
  readonly host: PortalHelpHost;
  readonly load: () => Promise<HelpWidgetModule>;
  readonly idle: IdleScheduler;
  /** The widget is on the page (its handle), or no longer (`null`). */
  readonly onHandle: (handle: HelpWidgetHandle | null) => void;
}

/** Mounts the widget while `mounted`, and removes it when that turns false or the root unmounts. Draws nothing itself. */
export function HelpMount({ mounted, host, load, idle, onHandle }: HelpMountProps): ReactNode {
  useEffect(() => {
    if (!mounted) return undefined;
    let cancelled = false;
    let handle: HelpWidgetHandle | null = null;
    const cancelIdle = idle(() => {
      load().then(
        (module: HelpWidgetModule | undefined) => {
          if (cancelled) return;
          if (module === undefined) {
            // Not loaded after all (see `present`): the Help item opens `/help`.
            host.onUnavailable();
            return;
          }
          handle = module.mountHelpWidget(host);
          if (handle === null) host.onUnavailable();
          onHandle(handle);
        },
        () => {
          // The launcher chunk was refused (a blocker, a dropped connection): the Help item opens `/help`.
          if (!cancelled) host.onUnavailable();
        },
      );
    });
    return () => {
      cancelled = true;
      cancelIdle();
      handle?.unmount();
      onHandle(null);
    };
  }, [mounted, host, load, idle, onHandle]);
  return null;
}
